// The following implementation is based on https://github.com/facebookresearch/segment-anything/blob/main/demo/src/components/helpers/onnxModelAPI.tsx

import type { Tensor } from 'onnxruntime-web';
import { Position } from '@clef/shared/types';

export interface ModelScale {
  samScale: number;
  height: number;
  width: number;
}

export enum ModelInputClickType {
  Add = 1,
  Remove = 0,
}

export const MODEL_INPUT_CLICK_TYPE_COLOR_LOOKUP: Record<ModelInputClickType, string> = {
  [ModelInputClickType.Add]: '#CFF000',
  [ModelInputClickType.Remove]: '#AD2E24',
};

export interface ModelInput {
  x: number;
  y: number;
  clickType: ModelInputClickType;
}

export interface ModeFeeds {
  imageEmbedding: Tensor;
  highResFeats0?: Tensor;
  highResFeats1?: Tensor;
  prompts?: Array<ModelInput>;
  modelScale: ModelScale;
}

// Input images to SAM must be resized so the longest side is 1024
const LONG_SIDE_LENGTH = 1024;

// Reference: https://github.com/facebookresearch/segment-anything/blob/main/demo/src/components/helpers/scaleHelper.tsx
export const getModelScale = (imageHeight: number, imageWidth: number): ModelScale => {
  const samScale = LONG_SIDE_LENGTH / Math.max(imageHeight, imageWidth);
  return { height: imageHeight, width: imageWidth, samScale };
};

// Reference: https://github.com/facebookresearch/segment-anything/blob/main/demo/src/components/helpers/onnxModelAPI.tsx
export const getModelFeeds = async ({
  prompts,
  imageEmbedding,
  highResFeats0,
  highResFeats1,
  modelScale,
}: ModeFeeds) => {
  let pointCoords;
  let pointLabels;
  let pointCoordsTensor;
  let pointLabelsTensor;

  const { Tensor } = await import('onnxruntime-web');

  // Check there are input click prompts
  if (prompts) {
    const n = prompts.length;

    // If there is no box input, a single padding point with
    // label -1 and coordinates (0.0, 0.0) should be concatenated
    // so initialize the array to support (n + 1) points.
    pointCoords = new Float32Array(2 * (n + 1));
    pointLabels = new Float32Array(n + 1);

    // Add prompts and scale to what SAM expects
    for (let i = 0; i < n; i++) {
      pointCoords[2 * i] = prompts[i].x * modelScale.samScale;
      pointCoords[2 * i + 1] = prompts[i].y * modelScale.samScale;
      pointLabels[i] = prompts[i].clickType;
    }

    // Add in the extra point/label when only prompts and no box
    // The extra point is at (0, 0) with label -1
    pointCoords[2 * n] = 0.0;
    pointCoords[2 * n + 1] = 0.0;
    pointLabels[n] = -1.0;

    // Create the tensor
    pointCoordsTensor = new Tensor('float32', pointCoords, [1, n + 1, 2]);
    pointLabelsTensor = new Tensor('float32', pointLabels, [1, n + 1]);
  }

  if (pointCoordsTensor === undefined || pointLabelsTensor === undefined) return;

  // There is no previous mask, so default to an empty tensor
  const maskInput = new Tensor('float32', new Float32Array(256 * 256), [1, 1, 256, 256]);
  // There is no previous mask, so default to 0
  const hasMaskInput = new Tensor('float32', [0]);

  const feeds: Record<string, Tensor> = {
    image_embed: imageEmbedding,
    point_coords: pointCoordsTensor,
    point_labels: pointLabelsTensor,
    mask_input: maskInput,
    has_mask_input: hasMaskInput,
  };

  // Add high resolution features if available, otherwise fallback to zeros
  if (highResFeats0) {
    feeds.high_res_feats_0 = highResFeats0;
  } else {
    // Fallback to zeros (for compatibility)
    feeds.high_res_feats_0 = new Tensor(
      'float32',
      new Float32Array(1 * 32 * 256 * 256),
      [1, 32, 256, 256],
    );
  }

  if (highResFeats1) {
    feeds.high_res_feats_1 = highResFeats1;
  } else {
    // Fallback to zeros (for compatibility)
    feeds.high_res_feats_1 = new Tensor(
      'float32',
      new Float32Array(1 * 64 * 128 * 128),
      [1, 64, 128, 128],
    );
  }

  return feeds;
};

/**
 * Converts a point from image coordinates to model input coordinates.
 * The model input takes the image at its original dimensions and centers it in a square
 * whose size equals the longer edge of the image.
 *
 * Example: 800×600 image
 * - Square size: 800×800 (using longer edge)
 * - Image is centered: no X offset, Y offset = (800 - 600) / 2 = 100
 * - Point (400, 300) → (400, 400)
 */
export const toModelInputPoint = (
  point: Position,
  imageSize?: { height: number; width: number } | null,
) => {
  if (!imageSize) {
    return point;
  }

  // The square size equals the longer edge of the image
  const longerEdge = Math.max(imageSize.height, imageSize.width);

  // Calculate offsets to center the image in the square
  const xOffset = (longerEdge - imageSize.width) / 2;
  const yOffset = (longerEdge - imageSize.height) / 2;

  // Add offsets to the point (no scaling needed)
  return {
    x: point.x + xOffset,
    y: point.y + yOffset,
  };
};

/**
 * Converts a point from image coordinates to model output coordinates.
 * The model output is square (e.g., 256x256), but the effective area depends on the image aspect ratio.
 *
 * For a non-square image, the image is scaled to fit the square output (maintaining aspect ratio)
 * and centered with padding.
 *
 * Example: 800×600 image with 256×256 model output
 * - Scale by longer edge: 800 (width is longer)
 * - Effective area in output: 256×192 (height scaled proportionally)
 * - Y offset: (256 - 192) / 2 = 32 pixels
 */
export const toModelOutputPoint = (
  point: Position,
  imageSize: { height: number; width: number },
  modelOutputSize: { height: number; width: number },
) => {
  // Scale based on the longer edge of the image
  const longerEdge = Math.max(imageSize.height, imageSize.width);
  const scale = modelOutputSize.width / longerEdge;

  // Effective dimensions in model output space (maintaining aspect ratio)
  const effectiveWidth = imageSize.width * scale;
  const effectiveHeight = imageSize.height * scale;

  // Calculate offsets (centering the effective area)
  const xOffset = (modelOutputSize.width - effectiveWidth) / 2;
  const yOffset = (modelOutputSize.height - effectiveHeight) / 2;

  // Convert point from image space to model output space
  const modelX = point.x * scale + xOffset;
  const modelY = point.y * scale + yOffset;

  return {
    x: Math.round(modelX),
    y: Math.round(modelY),
  };
};
